import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "../redux/store";
import ReactGA4 from "react-ga4";
import tinycolor from "tinycolor2";

import { AppointmentState, ServiceOption } from "../types";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import {
  parseQueryString,
  sendToWindowDataLayer,
  validateNumber,
} from "../utils/index";
import { compileTemplate } from "../utils/formatting";
import { ensureBookingId } from "../utils/bailout";
import { getAppointmentTheme } from "../utils/theme";
import { fetchFullSiteData } from "../functions/fetchFullSiteData";
import { UaEventOptions } from "react-ga4/types/ga4";
import {
  getFontAwesomeClassFromIconValue,
  isFontAwesomeServiceIcon,
  mapLegacySvgToFa,
} from "../constants/serviceIconMap";

interface ServiceOptionsProps {
  appointment: AppointmentState;
}

interface QueryStringParams {
  service?: string;
  shopId?: string;
  [key: string]: string | undefined;
}

const ServiceOptions: React.FC<ServiceOptionsProps> = ({ appointment }) => {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [queryString] = useState<QueryStringParams>(
    parseQueryString(window.location)
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [phoneNumber, setPhoneNumber] = useState<string>(
    appointment?.customer?.phone || ""
  );
  const [phoneError, setPhoneError] = useState<string>("");
  const [enteredPhoneOnServices, setEnteredPhoneOnServices] =
    useState<boolean>(false);
  const [failedServiceIcons, setFailedServiceIcons] = useState<
    Record<string, boolean>
  >({});
  const [creatingBookingId, setCreatingBookingId] = useState<boolean>(false);
  const [hasMoreServicesBelow, setHasMoreServicesBelow] =
    useState<boolean>(false);
  const lastServiceSentinelRef = useRef<HTMLDivElement | null>(null);
  const phoneCardRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();

  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );
  const accentLight = useMemo(() => tinycolor(accentColor).lighten(25).toHexString(), [accentColor]);
  const uncheckedCheckboxBackground = "#f3f4f6";
  const previousServiceSignatureRef = useRef<string>(
    appointment?.services?.length
      ? appointment.services.map((svc) => svc.name).sort().join("|")
      : appointment?.service || ""
  );

  const buildServiceUpdatePayload = (nextServices: ServiceOption[]) => {
    const nextServiceSignature = nextServices
      .map((svc) => svc.name)
      .sort()
      .join("|");
    const serviceChanged =
      previousServiceSignatureRef.current !== nextServiceSignature;

    previousServiceSignatureRef.current = nextServiceSignature;

    return {
      service:
        nextServices.length > 1
          ? "Multiple Services"
          : nextServices[0]?.name || "",
      hours: nextServices.reduce(
        (sum, service) => sum + (Number(service.hours) || 0),
        0
      ),
      dropOffOnly: nextServices.some((service) => service.dropOffOnly === true),
      services: nextServices,
      ...(serviceChanged
        ? {
            date: undefined,
            time: undefined,
            slot: undefined,
            complimentaryRide: undefined,
          }
        : {}),
    };
  };

  const handleServiceClick = (serviceOption: ServiceOption) => {
    const isSelected = selectedServices.includes(serviceOption.name);

    const updatedServices = isSelected
      ? selectedServices.filter((svc) => svc !== serviceOption.name)
      : [...selectedServices, serviceOption.name];

    const selectedServiceObjects = updatedServices
      .map((name) => serviceOptions.find((svc) => svc.name === name))
      .filter(Boolean) as ServiceOption[];
    const totalHours = selectedServiceObjects.reduce(
      (sum, service) => sum + (Number(service.hours) || 0),
      0
    );

    setSelectedServices(updatedServices);

    dispatch(
      updateAppointment({
        updates: {
          ...buildServiceUpdatePayload(selectedServiceObjects),
          serviceHours: appointmentDuration
            ? appointmentDuration / 60
            : totalHours,
          skipToForm: selectedServiceObjects.some(
            (service) => service.skipToForm
          ),
        },
      })
    );
  };

  // Nudges the list down by roughly one viewport when the "more services
  // below" arrow is tapped, rather than jumping straight to the bottom —
  // keeps some of the previously-visible cards in view so the scroll
  // doesn't feel like a jarring jump.
  const handleScrollHintClick = () => {
    const scrollRoot = lastServiceSentinelRef.current?.closest(
      '[data-testid="booking-stage"]'
    );
    scrollRoot?.scrollBy({ top: scrollRoot.clientHeight * 0.75, behavior: "smooth" });
  };

  let serviceOptions: ServiceOption[] = [];
  const imgPath = "https://bdm-files.s3.amazonaws.com/images/service-icons/";

  // The toggle means "use the service hours as the appointment duration".
  // When it is explicitly disabled, every appointment is fixed to
  // `staticAppointmentLength` minutes regardless of how many services are
  // selected. When it is enabled (or unset, the default), fall back to the
  // summed service hours.
  const appointmentDuration =
    appointment?.shopData?.useServiceHoursAsApptHours === false &&
    appointment?.shopData?.staticAppointmentLength
      ? appointment?.shopData?.staticAppointmentLength
      : undefined;

  if (appointment?.shopData?.services) {
    serviceOptions = appointment.shopData.services; // Custom Options
  } else if (appointment?.shopData?.serviceOptions) {
    serviceOptions = appointment.shopData.serviceOptions as ServiceOption[]; // Default Options
  }

  // Initialize selectedServices from appointment on component mount
  useEffect(() => {
    if (appointment?.services?.length) {
      setSelectedServices(appointment.services.map((svc) => svc.name));
    } else if (appointment?.service) {
      setSelectedServices([appointment.service]);
    }
  }, []);

  // Mobile-only "more services below" indicator: compares the bottom of the
  // card list (sentinel) against the sticky phone card's own top edge —
  // not just the scroll container's raw bounds, since the phone card sits
  // on top of (visually hides) whatever's scrolled beneath it even while
  // technically still inside the scrollable viewport. Desktop hides the
  // indicator via CSS (native scrollbar already shows overflow there).
  useEffect(() => {
    if (loading) return;
    const sentinel = lastServiceSentinelRef.current;
    const phoneCard = phoneCardRef.current;
    const scrollRoot = sentinel?.closest('[data-testid="booking-stage"]');
    if (!sentinel || !phoneCard || !scrollRoot) return;

    const recalculate = () => {
      const sentinelTop = sentinel.getBoundingClientRect().top;
      const phoneCardTop = phoneCard.getBoundingClientRect().top;
      setHasMoreServicesBelow(sentinelTop > phoneCardTop + 1);
    };

    recalculate();
    scrollRoot.addEventListener("scroll", recalculate, { passive: true });
    window.addEventListener("resize", recalculate);

    return () => {
      scrollRoot.removeEventListener("scroll", recalculate);
      window.removeEventListener("resize", recalculate);
    };
  }, [loading, serviceOptions?.length]);

  const captureUTM = (): Record<string, string> => {
    // Pull UTMs from the parent page's URL (forwarded by dcPortal.js into
    // sourceQueryString) instead of the iframe's own query string, which
    // only contains domain/groupname/shopId.
    const raw = appointment?.sourceQueryString || "";
    const stripped = raw.replace(/^[#?]+/, "").replace(/^\?/, "");
    const params = new URLSearchParams(stripped);
    const filteredData: Record<string, string> = {};
    params.forEach((value, key) => {
      if (key.startsWith("utm_")) filteredData[key] = value;
    });

    ReactGA4.event({
      category: "Appointments",
      action: "scheduler hit",
      label: "UTM DATA COLLECTED",
      utm_data: filteredData,
    } as UaEventOptions);

    return filteredData;
  };

  useEffect(() => {
    sendToWindowDataLayer(
      "dc-service-scheduler-hit",
      "dc-service-scheduler-hit"
    );

    if (!appointment?.gaUtmSent) {
      const utmData = captureUTM();

      dispatch(
        updateAppointment({
          updates: { gaUtmSent: true, utmData: utmData },
        })
      );
    }
  }, [appointment?.sourceQueryString]);

  // Helper function to process services and dispatch updates
  const processServicesAndDispatch = (servicesList: ServiceOption[]) => {
    if (servicesList.length > 0) {
      // Calculate total hours from all selected services
      const totalHours = servicesList.reduce(
        (sum, service) => sum + (Number(service.hours) || 0),
        0
      );

      // Set name based on number of services
      const serviceName =
        servicesList.length > 1 ? "Multiple Services" : servicesList[0].name;

      // Check if any service has dropOffOnly set to true
      const isDropOffOnly = servicesList.some(
        (service) => service.dropOffOnly === true
      );

      dispatch(
        updateAppointment({
          updates: {
            ...buildServiceUpdatePayload(servicesList),
            service: serviceName,
            hours: totalHours,
            dropOffOnly: isDropOffOnly,
          },
        })
      );

    }
  };

  // Handle query string service
  useEffect(() => {
    if (queryString?.service) {
      const selectedService = serviceOptions.find(
        (svc) => svc.name === queryString.service
      );

      if (selectedService) {
        // Initialize array with the service from queryString
        const initialServices = [selectedService];
        processServicesAndDispatch(initialServices);
      }
    }
  }, [queryString?.service, serviceOptions]);

  // Handle multiple selected services
  useEffect(() => {
    if (selectedServices.length > 0) {
      // Get all the selected service objects
      const selectedServiceObjects = selectedServices
        .map((serviceName) =>
          serviceOptions.find((svc) => svc.name === serviceName)
        )
        .filter(Boolean) as ServiceOption[];

      processServicesAndDispatch(selectedServiceObjects);
    }
  }, [selectedServices, serviceOptions]);

  useEffect(() => {
    const shopId = appointment.shopId || queryString.shopId || "";

    if (appointment.domain && shopId) {
      fetchFullSiteData(appointment.domain, shopId)
        .then((response) => {
          if (!appointment.bookingId) {
            dispatch(
              updateAppointment({
                updates: {
                  shopData: response,
                  bailed: false,
                },
              })
            );
          }
        })
        .then(() => {
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching site data:", error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedServices.length) {
      sendToWindowDataLayer("dc-service-option", selectedServices.join(", "));
    }
  }, [selectedServices]);

  // Automatically create booking ID when valid phone number is entered
  useEffect(() => {
    let isMounted = true;
    
    if (
      enteredPhoneOnServices &&
      validateNumber(phoneNumber) &&
      !appointment?.bookingId &&
      !creatingBookingId // Prevent duplicate calls while one is in-flight
    ) {
      const payload = {
        ...appointment,
        customer: { ...appointment?.customer, phone: phoneNumber },
      } as AppointmentState;
      
      setCreatingBookingId(true);
      
      ensureBookingId({
        appointment,
        payload,
        dispatch,
        updateAppointmentAction: updateAppointment,
      })
        .then(() => {
          if (isMounted) {
            setCreatingBookingId(false);
          }
        })
        .catch((error) => {
          console.error("Error creating booking ID from phone capture:", error);
          if (isMounted) {
            setCreatingBookingId(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [enteredPhoneOnServices, phoneNumber, appointment?.bookingId, creatingBookingId]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value);

    if (value) {
      const validatedPhone = validateNumber(value);
      if (!validatedPhone && value.trim()) {
        setPhoneError("Please enter a valid phone number");
        dispatch(
          updateAppointment({
            updates: {
              serviceOptionsPhoneCaptured: false,
            },
          })
        );
      } else {
        setPhoneError("");
        dispatch(
          updateAppointment({
            updates: {
              customer: {
                ...appointment?.customer,
                phone: value,
              },
              customerLookupProcessed: false,
              serviceOptionsPhoneCaptured: true,
              customerInputAutoAdvanced: false,
            },
          })
        );
        if (validateNumber(value)) {
          setEnteredPhoneOnServices(true);
        }
      }
    } else {
      setPhoneError("");
      dispatch(
        updateAppointment({
          updates: {
            customer: {
              ...appointment?.customer,
              phone: "",
            },
            customerLookupProcessed: false,
            serviceOptionsPhoneCaptured: false,
            customerInputAutoAdvanced: false,
          },
        })
      );
    }
  };

  const renderServiceIcon = (serviceOption: ServiceOption, isSelected: boolean) => {
    const iconValue = serviceOption.icon || "";
    const faClass = getFontAwesomeClassFromIconValue(iconValue);
    const legacyMappedFa = mapLegacySvgToFa(iconValue);
    const serviceIconKey = `${serviceOption.name}:${iconValue}`;

    // Box shrinks 44px -> 36px on mobile only (desktop keeps today's 44px via
    // the md: pair); glyph size stays 32px at both sizes per the mobile spec.
    const iconSizeClasses =
      "w-9 h-9 md:w-11 md:h-11 text-[32px] leading-9 md:leading-[44px] text-center inline-block";
    const iconColorStyle: React.CSSProperties = {
      color: isSelected ? "white" : accentColor,
    };

    if (!iconValue || failedServiceIcons[serviceIconKey]) {
      return (
        <i
          className={`fa-solid fa-gear ${iconSizeClasses}`}
          aria-hidden="true"
          style={iconColorStyle}
        />
      );
    }

    if (isFontAwesomeServiceIcon(iconValue) && faClass) {
      return (
        <i
          className={`${faClass} ${iconSizeClasses}`}
          aria-hidden="true"
          style={iconColorStyle}
        />
      );
    }

    if (legacyMappedFa) {
      return (
        <i
          className={`${legacyMappedFa} ${iconSizeClasses}`}
          aria-hidden="true"
          style={iconColorStyle}
        />
      );
    }

    return (
      <img
        src={`${imgPath}${iconValue}`}
        className="w-9 h-9 md:w-11 md:h-11"
        style={{
          filter: isSelected ? "invert(100%)" : undefined,
        }}
        alt={serviceOption.name}
        onError={() => {
          setFailedServiceIcons((prev) =>
            prev[serviceIconKey] ? prev : { ...prev, [serviceIconKey]: true }
          );
        }}
      />
    );
  };

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <i
              className="fa-solid fa-tire-rugged animate-spin text-6xl"
              aria-hidden="true"
              style={{ color: "#6b6b6b", lineHeight: 1 }}
            />
            <p className="text-sm text-gray-600 mt-3">
              Fetching service options...
            </p>
          </div>
        </div>
      ) : (
        <div
          className="px-[14px] md:px-6 md:pt-2 md:pb-0 flex flex-col min-h-full md:max-h-[68vh] md:overflow-hidden"
          style={{ background: gradientBackground }}
        >

          {/* Service selection divider */}
          <div className="flex items-center gap-3 mb-3 font-roboto">
            <div className="flex-1 h-px" style={{ backgroundColor: accentLight }} />
            <span
              className="text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              Select one or more services
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: accentLight }} />
          </div>

          {/* Service cards grid. On mobile this is plain flow content inside
              the single stage scroller (see PopupWidget.tsx); desktop keeps
              its own independent inner scroll region exactly as before. */}
          <div className="md:flex-1 md:min-h-0 md:overflow-y-auto md:pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px] md:gap-3 pb-3">
              {serviceOptions?.map((serviceOption, index) => {
                const isSelected = selectedServices.includes(serviceOption.name);
                return (
                  <div
                    key={`${serviceOption.name}-${index}`}
                    service-id={`dc-service-${serviceOption.name
                      .replace(/\s+/g, "_")
                      .toLowerCase()}`}
                    data-testid="service-option"
                    data-service-name={serviceOption.name}
                    onClick={() => handleServiceClick(serviceOption)}
                    className="flex items-center gap-[10px] md:gap-3 rounded-[12px] md:rounded-[16px] p-[10px] md:p-3 cursor-pointer transition-shadow font-roboto"
                    style={{
                      backgroundColor: isSelected ? accentColor : "#ffffff",
                      border: isSelected
                        ? `2px solid ${accentLight}`
                        : "2px solid transparent",
                      boxShadow: isSelected
                        ? `0 6px 16px ${accentColor}55`
                        : "0 1px 4px rgba(0,0,0,0.08)",
                    }}
                  >
                    {/* Checkbox */}
                    <div className="flex-shrink-0">
                      <div
                        className="flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-[6px] md:rounded-md"
                        style={{
                          border: `2px solid ${isSelected ? "transparent" : accentColor}`,
                          backgroundColor: isSelected ? "rgba(255,255,255,0.25)" : uncheckedCheckboxBackground,
                        }}
                      >
                        {isSelected && (
                          <i
                            className="fa-solid fa-check"
                            aria-hidden="true"
                            style={{ color: "#ffffff", fontSize: "14px", lineHeight: 1 }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 md:w-11 md:h-11">
                      {renderServiceIcon(serviceOption, isSelected)}
                    </div>

                    {/* Name & Description */}
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-bold leading-tight"
                        style={{ color: isSelected ? "#ffffff" : accentColor }}
                      >
                        {serviceOption.name}
                      </div>
                      {serviceOption.description && (
                        <div
                          className="mt-0.5 text-xs leading-snug"
                          style={{
                            color: isSelected ? "rgba(255,255,255,0.85)" : "#6b7280",
                          }}
                        >
                          {serviceOption.description}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Marks the true bottom of the service list so the "more
                services below" arrow (rendered near the phone card) knows
                when there's nothing left to scroll to. */}
            <div ref={lastServiceSentinelRef} aria-hidden="true" />
          </div>

          {/* Divider line before phone section */}
          <div className="flex items-center gap-3 mt-1 mb-1 md:mt-3 md:mb-3 font-roboto flex-shrink-0">
            <div className="flex-1 h-px" style={{ backgroundColor: accentLight }} />
          </div>

          {/* Customer Phone & SMS section - always visible at bottom */}
          {(() => {
            const isSmsOptedIn = appointment?.customer?.optOut === false;
            const smsShopName = appointment?.shopData?.shopName?.name || "my service center";
            const smsOptInTitle = appointment?.shopData?.messages?.smsOptInTitle || "Receive Appointment Confirmation Reminders";
            const smsOptInMessageRaw =
              appointment?.shopData?.messages?.smsOptInMessage ||
              "By checking this box, I agree to receive informational SMS messages from {{shop_name}} regarding appointment reminders and service updates. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not required to book service.";
            const smsOptInMessageCompiled = compileTemplate(smsOptInMessageRaw, { shop_name: smsShopName });

            return (
              <div
                ref={phoneCardRef}
                className="svc-phone-card sticky bottom-0 mt-auto md:static md:mt-0 mx-[-14px] md:mx-0 rounded-t-[14px] rounded-b-none md:rounded-[16px] p-2.5 pb-2.5 md:p-3 md:pb-5 md:mb-2 font-roboto flex-shrink-0"
                style={
                  {
                    backgroundColor: accentColor,
                    border: `2px solid ${accentLight}`,
                    "--phone-shadow": `0 6px 16px ${accentColor}55`,
                  } as React.CSSProperties
                }
              >
                {/* Mobile-only "more services below" indicator — only
                    appears while there's more of the list left to scroll to;
                    disappears once the last card is visible. Tapping it
                    scrolls the list down rather than just sitting there as a
                    static hint. Desktop already has a native scrollbar as
                    its own affordance. */}
                {hasMoreServicesBelow && (
                  <button
                    type="button"
                    onClick={handleScrollHintClick}
                    className="absolute -top-[18px] left-1/2 flex h-9 w-9 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border-none p-0 transition-opacity hover:opacity-90 active:opacity-80 md:hidden"
                    style={{
                      backgroundColor: "#ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    }}
                    aria-label="Scroll down to see more services"
                  >
                    <i
                      className="fa-solid fa-chevron-down"
                      aria-hidden="true"
                      style={{ color: accentColor, fontSize: "14px", lineHeight: 1 }}
                    />
                  </button>
                )}

                {/* Phone number row. md: (768px), not sm: (640px) — matches
                    the mobile/desktop split used everywhere else here, so a
                    viewport between 640-768px still gets the full-width
                    mobile input instead of prematurely switching to the
                    narrow fixed-width desktop layout. */}
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-2 md:mb-3">
                  <div className="w-full md:w-auto md:flex-shrink-0 md:max-w-[200px]">
                    <input
                      id="service-phone-input"
                      type="tel"
                      placeholder="Phone Number"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      className="w-full rounded-md px-3 py-2 text-sm outline-none focus:outline-none focus:ring-0 box-border appearance-none placeholder:text-[#9ca3af]"
                      data-testid="service-phone-input"
                      style={{
                        border: `2px solid ${phoneError ? "#ef4444" : accentLight}`,
                        boxShadow: "none",
                        WebkitAppearance: "none",
                        appearance: "none",
                        color: "#111827",
                      }}
                      aria-label="Phone Number"
                    />
                    {phoneError && (
                      <div className="text-red-200 text-xs mt-1">{phoneError}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-xs leading-snug"
                      style={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Helps us look up past vehicle records, if available.
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div
                  className="my-2 md:my-3 h-px"
                  style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
                />

                {/* SMS Opt-In row */}
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => {
                    dispatch(
                      updateAppointment({
                        updates: {
                          customer: {
                            ...appointment?.customer,
                            optOut: appointment?.customer?.optOut === false ? true : false,
                          },
                        },
                      })
                    );
                  }}
                >
                  <div className="flex-shrink-0 self-start mt-0.5">
                    <div
                      className="flex items-center justify-center w-5 h-5 md:w-7 md:h-7 rounded-md"
                      style={{
                        border: "2px solid rgba(255,255,255,0.5)",
                        backgroundColor: isSmsOptedIn ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)",
                      }}
                    >
                      {isSmsOptedIn && (
                        <i
                          className="fa-solid fa-check text-[10px] md:text-sm"
                          aria-hidden="true"
                          style={{ color: "#ffffff", lineHeight: 1 }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-xs md:text-sm font-bold leading-tight"
                      style={{ color: "#ffffff" }}
                    >
                      {smsOptInTitle}
                    </div>
                    <div
                      className="mt-0.5 text-[10px] md:text-xs leading-snug"
                      style={{ 
                        color: "rgba(255,255,255,0.9)",
                      } as React.CSSProperties}
                    >
                      {smsOptInMessageCompiled}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}        </div>
      )}
    </>
  );
};

export { ServiceOptions };
